import { fetchBanWords } from './fetchBanWords';
import { filterBanWords } from './filterBanWords';
import type { BanWordsRegexPatterns } from './utils';
import { createRegexPatterns } from './utils';

/**
 * React 밖(core/game 등)에서 쓰는 동기 금지어 마스킹.
 * 룰 소스(`fetchBanWords`)는 `useZepBanWords` 와 모듈 캐시를 공유한다.
 */
let patterns: BanWordsRegexPatterns | null = null;
let initPromise: Promise<void> | null = null;

/**
 * 워밍업 입력 — **ASCII 와 non-Latin-1 을 모두 넣어야 한다.**
 * V8 은 문자열 표현별(one-byte/two-byte)로 정규식을 따로 컴파일하므로, 한쪽만
 * 예열하면 다른 쪽 첫 이름이 컴파일 비용을 문다(실측 프레임 37.4ms → 2.0ms).
 * 선행 공백은 isolatedRegex 의 `(^|\s)` 분기까지 예열한다.
 */
const WARMUP_INPUTS = ['warmup abc 1', ' 가나다 abc 1'];

// 이름별 결과 memo — refresh 마다 재마스킹하는 비용(~0.13ms/이름)을 없앤다.
// 룰은 세션 중 불변이라 무효화 불필요.
const MASK_CACHE_MAX = 2000;
const maskCache = new Map<string, string>();

// 룰 로드 성공 시 1회 통지. 콜드 스타트로 원문 표시된 이름을 재마스킹하는 소비자가 구독.
const loadListeners = new Set<() => void>();

const notifyLoaded = () => {
  // 사본 순회 — 콜백 안에서 구독 해제해도 안전하게.
  for (const listener of [...loadListeners]) {
    try {
      listener();
    } catch {
      // 한 리스너의 예외가 전체 통지를 깨지 않도록 격리.
    }
  }
};

/**
 * 룰 로드 성공 시 1회 호출될 리스너를 등록한다(이미 로드됐으면 즉시 1회). 해제 함수 반환.
 *
 * `prefetchBanWords().then(...)` 으로 대체 불가 — prefetch 는 실패해도 fail-open 으로
 * resolve 되고, 이후 재시도 성공엔 콜백이 안 붙어 이름이 원문으로 남는다.
 */
export const onBanWordsLoaded = (listener: () => void): (() => void) => {
  if (patterns) {
    listener();
    return () => {};
  }
  loadListeners.add(listener);
  return () => {
    loadListeners.delete(listener);
  };
};

/**
 * 룰을 백그라운드 로드해 패턴을 캐시한다. 게임 씬 생성 시 호출하면 첫 렌더 전에
 * 준비되고, 정규식 컴파일 비용(저사양 모바일 100ms+)도 로딩 구간에서 소화된다.
 */
export const prefetchBanWords = (): Promise<void> => {
  if (patterns) return Promise.resolve();
  if (initPromise) return initPromise;

  initPromise = fetchBanWords()
    .then(data => {
      patterns = createRegexPatterns(data);
      // 컴파일 비용을 로딩 구간에 흡수 (WARMUP_INPUTS 주석 참고).
      for (const input of WARMUP_INPUTS) filterBanWords(input, patterns);
      notifyLoaded();
    })
    .catch(() => {
      // 로드 실패 시 fail-open(원문 유지). 다음 호출에서 재시도한다.
    })
    .finally(() => {
      initPromise = null;
    });

  return initPromise;
};

/**
 * 동기 금지어 마스킹. 룰 미로드 시 원문 반환(fail-open) + 백그라운드 로드 트리거.
 *
 * ⚠️ **표시** 마스킹이다 — 서버 원본은 그대로이며 이 클라이언트 화면에서만 가려진다.
 */
export const maskBanWords = (text: string): string => {
  if (typeof text !== 'string' || text.length === 0) return text;
  if (!patterns) {
    void prefetchBanWords();
    return text;
  }

  const cached = maskCache.get(text);
  if (cached !== undefined) return cached;

  const masked = filterBanWords(text, patterns);
  // 전체 clear 는 hot 이름까지 날려 다음 버스트에서 전부 재계산된다(실측 ~268ms 히칭).
  // Map 은 삽입 순서를 보존하므로 가장 오래된 하나만 버린다.
  if (maskCache.size >= MASK_CACHE_MAX) {
    const oldest = maskCache.keys().next().value;
    if (oldest !== undefined) maskCache.delete(oldest);
  }
  maskCache.set(text, masked);
  return masked;
};
